// Which gateway pays for an order is the backend's call (Ansa-Support#113):
// POST api/payment/checkout answers { data: { gateway: "ipay88" } } or
// { data: { gateway: "rm", checkoutUrl } }, so Revenue Monster can be switched
// on and rolled back server-side without a web or app release.
//
//   ipay88 / 404  the page's existing iPay88 code runs unchanged. A 404 is an
//                 older backend without the route, or an unknown order, which
//                 the makepayment call then reports exactly as it does today.
//   rm            the page does its usual hand-off steps and this same tab goes
//                 to the checkout URL. Never a new window: inside the
//                 superapp's WebView that breaks the return.
//   anything else an error, never iPay88: after a 409 (already paid, payment in
//                 progress, not payable), a 5xx, a network failure or an answer
//                 we cannot trust, falling back could charge the customer twice.
//
// Self-contained on purpose: this line has none of main's payment helpers.

export const PAYMENT_GATEWAY = Object.freeze({ IPAY88: "ipay88", RM: "rm" });

export const CHECKOUT_ERROR = Object.freeze({
	MALFORMED_ORDER: "malformed_order",
	NOT_PAYABLE: "order_not_payable",
	UNAVAILABLE: "gateway_unavailable",
	MALFORMED_ANSWER: "malformed_gateway_answer",
	UNTRUSTED_CHECKOUT_URL: "untrusted_checkout_url",
});

export class GatewayCheckoutError extends Error {
	constructor(code) {
		super(code);
		this.name = "GatewayCheckoutError";
		this.code = code;
	}
}

const fail = (code) => {
	throw new GatewayCheckoutError(code);
};

const DEFAULT_RM_CHECKOUT_ORIGINS = "https://pg.revenuemonster.my";
const DNS_HOSTNAME = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const PRINTABLE_ASCII = /^[\x21-\x7e]+$/;
const MAX_URL_LENGTH = 2048;

/**
 * The exact origins a Revenue Monster checkout may live on, from
 * REACT_APP_RM_CHECKOUT_ORIGINS (comma-separated). Unset means the RM
 * production gateway. A malformed entry is dropped rather than breaking the
 * app; if none is left, no RM checkout URL is trusted.
 */
export const parseRmCheckoutOrigins = (raw) => {
	const value = String(raw ?? "").trim();
	if (!value) return [DEFAULT_RM_CHECKOUT_ORIGINS];
	const origins = value
		.split(",")
		.map((entry) => entry.trim())
		.filter(Boolean)
		.flatMap((entry) => {
			if (!PRINTABLE_ASCII.test(entry)) return [];
			let parsed;
			try {
				parsed = new URL(entry);
			} catch {
				return [];
			}
			if (
				parsed.protocol !== "https:" || parsed.username || parsed.password || parsed.port ||
				parsed.pathname !== "/" || parsed.search || parsed.hash || !DNS_HOSTNAME.test(parsed.hostname)
			) {
				return [];
			}
			return [parsed.origin];
		});
	return [...new Set(origins)];
};

// CRA inlines REACT_APP_* at build time.
export const configuredRmCheckoutOrigins = () =>
	parseRmCheckoutOrigins(process.env.REACT_APP_RM_CHECKOUT_ORIGINS);

/**
 * The checkout URL, normalized, when it is printable-ASCII HTTPS with no
 * credentials, no port but the default, and an exactly allowed origin; else null.
 */
export const getTrustedRmCheckoutUrl = (candidate, allowedOrigins = configuredRmCheckoutOrigins()) => {
	if (
		typeof candidate !== "string" ||
		candidate.length > MAX_URL_LENGTH ||
		!PRINTABLE_ASCII.test(candidate) ||
		candidate.includes("\\") ||
		!/^https:\/\//i.test(candidate)
	) {
		return null;
	}
	try {
		const parsed = new URL(candidate);
		if (
			parsed.protocol !== "https:" ||
			parsed.username ||
			parsed.password ||
			parsed.port ||
			!allowedOrigins.includes(parsed.origin)
		) {
			return null;
		}
		return parsed.href;
	} catch {
		return null;
	}
};

const isPlainObject = (value) =>
	value !== null &&
	typeof value === "object" &&
	!Array.isArray(value) &&
	Object.getPrototypeOf(value) === Object.prototype;

/**
 * Reads the backend's answer into { gateway: "ipay88" } or
 * { gateway: "rm", checkoutUrl }, or throws: an RM answer whose URL is not
 * trusted is an error, not a reason to use iPay88.
 */
export const validateGatewayAnswer = (body, allowedOrigins = configuredRmCheckoutOrigins()) => {
	if (!isPlainObject(body) || body.success !== true || !isPlainObject(body.data)) {
		fail(CHECKOUT_ERROR.MALFORMED_ANSWER);
	}
	const { gateway } = body.data;
	if (gateway === PAYMENT_GATEWAY.IPAY88) return Object.freeze({ gateway });
	if (gateway !== PAYMENT_GATEWAY.RM) fail(CHECKOUT_ERROR.MALFORMED_ANSWER);
	const checkoutUrl = getTrustedRmCheckoutUrl(body.data.checkoutUrl, allowedOrigins);
	if (!checkoutUrl) fail(CHECKOUT_ERROR.UNTRUSTED_CHECKOUT_URL);
	return Object.freeze({ gateway, checkoutUrl });
};

const askGateway = async (request, allowedOrigins) => {
	let body;
	try {
		body = await request();
	} catch (error) {
		// An axios error: the status is on error.response (none for a network failure).
		const status = error?.response?.status;
		if (status === 404) return Object.freeze({ gateway: PAYMENT_GATEWAY.IPAY88 });
		if (status === 409) fail(CHECKOUT_ERROR.NOT_PAYABLE);
		fail(CHECKOUT_ERROR.UNAVAILABLE);
	}
	return validateGatewayAnswer(body, allowedOrigins);
};

/**
 * Leaves for the Revenue Monster checkout in this same tab.
 */
export const leaveForRmCheckout = (url, browserWindow = window) => {
	browserWindow.location.assign(url);
};

// One checkout start per order: a double tap must not open two checkouts.
const checkoutRequests = new Map();

/**
 * Starts paying for `orderNumber` through the gateway the backend picks.
 *
 * `request` asks the backend (requestPaymentGateway with the page's token).
 * `payWithIpay88` is the page's existing iPay88 code, run unchanged when the
 * answer is iPay88 or a 404. `beforeLeave` is the page's own hand-off step,
 * run just before this tab leaves for Revenue Monster. Rejects with a
 * GatewayCheckoutError (or whatever the iPay88 code throws), after which the
 * order may be tried again.
 */
export const startPaymentCheckoutOnce = (
	orderNumber,
	{
		request,
		payWithIpay88,
		beforeLeave = () => {},
		navigate = leaveForRmCheckout,
		allowedOrigins = configuredRmCheckoutOrigins(),
	} = {}
) => {
	if (typeof orderNumber !== "string" || orderNumber.length < 1 || orderNumber.length > 64) {
		return Promise.reject(new GatewayCheckoutError(CHECKOUT_ERROR.MALFORMED_ORDER));
	}
	if (checkoutRequests.has(orderNumber)) return checkoutRequests.get(orderNumber);

	const operation = askGateway(request, allowedOrigins)
		.then(async (decision) => {
			if (decision.gateway === PAYMENT_GATEWAY.IPAY88) {
				await payWithIpay88();
				return decision;
			}
			beforeLeave();
			navigate(decision.checkoutUrl);
			return decision;
		})
		.catch((error) => {
			checkoutRequests.delete(orderNumber);
			throw error;
		});

	checkoutRequests.set(orderNumber, operation);
	return operation;
};

export const isGatewayCheckoutError = (error) => error instanceof GatewayCheckoutError;
